import { Injectable, computed, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AppUnavailableService } from '../async-load/app-unavailable.service';
import {
  isConnectivityFailure,
  isServerUnavailable,
  isSessionAuthFailure,
  shouldUseServiceUnavailablePage,
  userFacingMessage,
} from '../errors/user-facing-error';
import { Officer, OfficerRole, PortalType } from '../models/officer.model';
import { ApiClientService } from './api-client.service';
import { clearAllAskAiBrowserStorage } from './ask-ai-storage';
import { homeRouteForOfficer } from '../routing/portal-home';
import { readDeskLang } from '../i18n/role-desk.i18n';

const SESSION_KEY = 'jansunwai.employee.session';
const TOKEN_KEY = 'jansunwai.employee.token';

interface ApiSession {
  accountId: number;
  ipId: string;
  role: OfficerRole | string;
  activeRole?: OfficerRole | string | null;
  roles?: Array<OfficerRole | string>;
  homeRoute?: string;
  needsRoleChoice?: boolean;
  partyId?: string;
  officerId?: string | number;
  userId?: string | number;
  displayName?: string;
  employeeId?: string;
  email?: string;
  phone?: string;
  department?: string;
  district?: string;
  actorType?: string;
  portalType?: PortalType;
  citizenPortalAvailable?: boolean;
  permissionCodes?: string[];
  fullAccess?: boolean;
  hasPassword?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly api = inject(ApiClientService);
  private readonly appUnavailable = inject(AppUnavailableService);

  /** Stored claims are only a hint until GET /auth/session confirms them. */
  private readonly session = signal<Officer | null>(this.readSession());
  private readonly accessToken = signal<string | null>(this.readToken());
  private readonly sessionVerified = signal(false);
  private resolveSessionReady: () => void = () => undefined;
  private readonly sessionReady = new Promise<void>((resolve) => {
    this.resolveSessionReady = resolve;
  });

  readonly officer = computed(() => this.session());
  /** True only after the stored token is confirmed (or there was nothing to restore). */
  readonly isAuthenticated = computed(
    () => this.sessionVerified() && this.session() !== null && !!this.accessToken(),
  );
  readonly token = computed(() => this.accessToken());

  /** True when GET /auth/session failed with network/server error but local credentials remain. */
  readonly sessionOffline = signal(false);

  constructor() {
    // Defer the probe so construction finishes before the interceptor injects this service.
    queueMicrotask(() => void this.restoreSession());
  }

  /** Resolves once leftover storage has been confirmed or cleared. */
  whenSessionReady(): Promise<void> {
    return this.sessionReady;
  }

  hasStoredSessionCredentials(): boolean {
    return !!this.accessToken() && !!this.session();
  }

  /** Re-probe GET /auth/session after a connectivity failure (manual retry only). */
  async retrySessionRestore(): Promise<'ok' | 'auth_rejected' | 'offline'> {
    if (!this.hasStoredSessionCredentials()) {
      this.sessionOffline.set(false);
      return 'auth_rejected';
    }
    try {
      const token = this.accessToken()!;
      const data = await firstValueFrom(this.api.get<{ session: ApiSession }>('/auth/session'));
      this.applySession(this.mapSession(data.session), token);
      this.sessionOffline.set(false);
      this.appUnavailable.clear();
      return 'ok';
    } catch (err) {
      if (isSessionAuthFailure(err)) {
        this.clearLocalSession();
        return 'auth_rejected';
      }
      if (isConnectivityFailure(err) || isServerUnavailable(err)) {
        this.markSessionOffline();
        return 'offline';
      }
      this.clearLocalSession();
      return 'auth_rejected';
    }
  }

  /** Admin portal login (Admin + desk profiles created by Admin). */
  async loginAdmin(
    username: string,
    password: string,
  ): Promise<
    { ok: true } | { ok: false; message: string; serverUnavailable?: boolean }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{ token: string; session: ApiSession }>('/auth/admin/login', {
          username,
          password,
        }),
      );
      if (data.session.role === 'citizen') {
        return { ok: false, message: 'Use Citizen login for citizen accounts.' };
      }
      const officer = this.mapSession(data.session);
      this.applySession(officer, data.token);
      return { ok: true };
    } catch (err) {
      const message = this.errMessage(err);
      if (shouldUseServiceUnavailablePage(err)) {
        return { ok: false, message, serverUnavailable: true };
      }
      return { ok: false, message };
    }
  }

  /** Compat — same as loginAdmin. */
  async login(username: string, password: string) {
    return this.loginAdmin(username, password);
  }

  /**
   * Request OTP for any desk user (email or mobile).
   * Citizen accounts are rejected by the API.
   */
  async requestOfficerOtp(
    otpChannel: 'email' | 'mobile' = 'email',
    destination: string,
  ): Promise<
    | {
        ok: true;
        challengeId: string;
        destinationMasked: string;
        otpChannel: string;
        expiresInSeconds: number;
        resendAvailableInSeconds: number;
      }
    | { ok: false; message: string; serverUnavailable?: boolean }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{
          challengeId: string;
          destinationMasked: string;
          otpChannel: string;
          otpSentTo: string;
          expiresInSeconds: number;
          resendAvailableInSeconds?: number;
        }>('/auth/officer/otp/request', { otpChannel, destination }),
      );
      return {
        ok: true,
        challengeId: data.challengeId,
        destinationMasked: data.destinationMasked,
        otpChannel: data.otpChannel || data.otpSentTo,
        expiresInSeconds: data.expiresInSeconds,
        resendAvailableInSeconds: data.resendAvailableInSeconds ?? 60,
      };
    } catch (err) {
      const message = this.errMessage(err);
      if (shouldUseServiceUnavailablePage(err)) {
        return { ok: false, message, serverUnavailable: true };
      }
      return { ok: false, message };
    }
  }

  async resendOfficerOtp(
    challengeId: string,
  ): Promise<
    | {
        ok: true;
        challengeId: string;
        destinationMasked: string;
        expiresInSeconds: number;
        resendAvailableInSeconds: number;
      }
    | { ok: false; message: string; serverUnavailable?: boolean }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{
          challengeId: string;
          destinationMasked: string;
          expiresInSeconds: number;
          resendAvailableInSeconds?: number;
        }>('/auth/officer/otp/resend', { challengeId }),
      );
      return {
        ok: true,
        challengeId: data.challengeId,
        destinationMasked: data.destinationMasked,
        expiresInSeconds: data.expiresInSeconds,
        resendAvailableInSeconds: data.resendAvailableInSeconds ?? 60,
      };
    } catch (err) {
      const message = this.errMessage(err);
      if (shouldUseServiceUnavailablePage(err)) {
        return { ok: false, message, serverUnavailable: true };
      }
      return { ok: false, message };
    }
  }

  async verifyOfficerOtp(
    challengeId: string,
    otp: string,
  ): Promise<
    { ok: true } | { ok: false; message: string; serverUnavailable?: boolean }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{ token: string; session: ApiSession }>('/auth/officer/otp/verify', {
          challengeId,
          otp,
        }),
      );
      if (data.session.role === 'citizen') {
        return { ok: false, message: 'Use Citizen login for citizen accounts.' };
      }
      this.applySession(this.mapSession(data.session), data.token);
      return { ok: true };
    } catch (err) {
      const message = this.errMessage(err);
      if (shouldUseServiceUnavailablePage(err)) {
        return { ok: false, message, serverUnavailable: true };
      }
      return { ok: false, message };
    }
  }

  /**
   * Citizen OTP login. Citizen portal is not built — does not enter Employee Portal.
   */
  async loginCitizen(
    mobile: string,
    otp: string,
    challengeId: string,
  ): Promise<
    | { ok: true; citizenPortalAvailable: false; message: string }
    | { ok: false; message: string }
  > {
    try {
      const data = await firstValueFrom(
        this.api.post<{
          token: string;
          session: ApiSession;
          citizenPortalAvailable?: boolean;
          message?: string;
        }>('/auth/citizen/otp/verify', { challengeId, otp, mobile }),
      );
      return {
        ok: true,
        citizenPortalAvailable: false,
        message: data.message || 'Citizen authenticated.',
      };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  async sendCitizenOtp(mobile: string): Promise<{ ok: true; challengeId: string } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.post<{ challengeId: string }>('/auth/citizen/otp/send', { mobile }),
      );
      return { ok: true, challengeId: data.challengeId };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  /** Drop local session without calling logout API (e.g. after 401). */
  clearLocalSession(): void {
    this.sessionOffline.set(false);
    this.appUnavailable.clear();
    this.session.set(null);
    this.accessToken.set(null);
    this.sessionVerified.set(false);
    localStorage.removeItem(SESSION_KEY);
    localStorage.removeItem(TOKEN_KEY);
    // Ask AI session + turns must never survive into the next account on this browser.
    clearAllAskAiBrowserStorage();
  }

  async logout(): Promise<void> {
    // Subscribe first so the interceptor still sees the token and the server can
    // revoke it, but drop the local session without waiting: a slow or hanging
    // revocation must not leave the UI rendering a signed-in shell.
    const revoked = this.accessToken()
      ? firstValueFrom(this.api.post('/auth/admin/logout', {})).catch(() => undefined)
      : Promise.resolve(undefined);
    this.clearLocalSession();
    await revoked;
  }

  /**
   * Persists name/email/phone to the DB via PATCH /auth/profile and applies the
   * server's returned session+token locally. A local-only signal/localStorage
   * patch (the old behavior here) looks like it worked but reverts the moment
   * anything re-reads the real session from the server (e.g. a fresh login, or
   * the token reissue that changePassword already does).
   */
  async updateProfile(
    patch: Partial<Pick<Officer, 'name' | 'phone' | 'email'>>,
  ): Promise<{ ok: true; message: string } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.patch<{ ok: boolean; message: string; token: string; session: ApiSession }>(
          '/auth/profile',
          { name: patch.name, email: patch.email, phone: patch.phone },
        ),
      );
      if (data.token && data.session) {
        this.applySession(this.mapSession(data.session), data.token);
      } else {
        if (data.token) {
          this.accessToken.set(data.token);
          localStorage.setItem(TOKEN_KEY, data.token);
        }
        if (data.session) {
          const officer = this.mapSession(data.session);
          this.session.set(officer);
          localStorage.setItem(SESSION_KEY, JSON.stringify(officer));
        }
        this.sessionVerified.set(true);
      }
      return { ok: true, message: data.message || 'Profile updated.' };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  /**
   * Change password via JWT-authenticated API.
   * Backend verifies current password, stores bcrypt hash in MariaDB, returns a fresh JWT.
   */
  async changePassword(args: {
    newPassword: string;
    currentPassword?: string;
    otpChallengeId?: string;
    otp?: string;
  }): Promise<{ ok: true; message: string } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.post<{ ok: boolean; message: string; token: string; session: ApiSession }>(
          '/auth/change-password',
          {
            newPassword: args.newPassword,
            currentPassword: args.currentPassword,
            otpChallengeId: args.otpChallengeId,
            otp: args.otp,
          },
        ),
      );
      if (data.token && data.session) {
        this.applySession(this.mapSession(data.session), data.token);
      } else {
        if (data.token) {
          this.accessToken.set(data.token);
          localStorage.setItem(TOKEN_KEY, data.token);
        }
        if (data.session) {
          const officer = this.mapSession(data.session);
          this.session.set(officer);
          localStorage.setItem(SESSION_KEY, JSON.stringify(officer));
        }
        this.sessionVerified.set(true);
      }
      return { ok: true, message: data.message || 'Password changed successfully.' };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  hasRole(...roles: OfficerRole[]): boolean {
    const current = this.session();
    if (!current) return false;
    const held = current.roles?.length ? current.roles : [current.role];
    return roles.some((r) => held.includes(r));
  }

  readonly assignedRoles = computed(() => {
    const o = this.session();
    if (!o) return [] as string[];
    return o.roles?.length ? [...o.roles] : o.role ? [o.role] : [];
  });

  needsRoleChoice(): boolean {
    const o = this.session();
    if (!o) return false;
    const roles = o.roles?.length ? o.roles : o.role ? [o.role] : [];
    return roles.length > 1 && !o.activeRole;
  }

  async switchActiveRole(
    role: string,
  ): Promise<{ ok: true; homeRoute: string } | { ok: false; message: string }> {
    try {
      const data = await firstValueFrom(
        this.api.post<{ token: string; session: ApiSession; homeRoute?: string }>(
          '/auth/active-role',
          { role },
        ),
      );
      const officer = this.mapSession(data.session);
      this.applySession(officer, data.token);
      return { ok: true, homeRoute: data.homeRoute || homeRouteForOfficer(officer) };
    } catch (err) {
      return { ok: false, message: this.errMessage(err) };
    }
  }

  private applySession(officer: Officer, token: string): void {
    this.session.set(officer);
    this.accessToken.set(token);
    this.sessionVerified.set(true);
    localStorage.setItem(SESSION_KEY, JSON.stringify(officer));
    localStorage.setItem(TOKEN_KEY, token);
  }

  /**
   * Leftover localStorage must not count as signed-in until the API agrees.
   * Otherwise the public landing CTA flashes "Go to dashboard" on first open
   * after a server restart or a dead token, then snaps back to Login.
   */
  private async restoreSession(): Promise<void> {
    try {
      const token = this.accessToken();
      const stored = this.session();
      if (!token || !stored) {
        this.clearLocalSession();
        return;
      }
      const data = await firstValueFrom(this.api.get<{ session: ApiSession }>('/auth/session'));
      this.applySession(this.mapSession(data.session), token);
      this.sessionOffline.set(false);
      this.appUnavailable.clear();
    } catch (err) {
      if (isSessionAuthFailure(err)) {
        this.clearLocalSession();
      } else if (isConnectivityFailure(err) || isServerUnavailable(err)) {
        this.markSessionOffline();
      } else {
        this.clearLocalSession();
      }
    } finally {
      this.resolveSessionReady();
    }
  }

  private markSessionOffline(): void {
    this.sessionVerified.set(true);
    this.sessionOffline.set(true);
    this.appUnavailable.showForFailedRead(() => this.retrySessionRestore());
  }

  private mapSession(s: ApiSession & { designation?: string }): Officer {
    const roles = (Array.isArray(s.roles) && s.roles.length
      ? s.roles
      : [(s.role as OfficerRole) || 'officer']) as OfficerRole[];
    const activeRole = (s.activeRole as OfficerRole | null | undefined) ?? null;
    const primary = (activeRole || (s.role as OfficerRole) || roles[0] || 'officer') as OfficerRole;
    const officer: Officer = {
      id: s.userId ?? s.officerId ?? s.partyId ?? s.ipId ?? 'user',
      employeeId: String(s.employeeId || s.officerId || s.userId || 'EMP'),
      name: s.displayName || 'User',
      email: s.email || '',
      phone: s.phone || '',
      role: primary,
      activeRole,
      roles,
      department: s.department || 'Unassigned',
      district: s.district || 'Lucknow',
      portalType: s.portalType || 'admin',
      designation: s.designation,
      permissionCodes: Array.isArray(s.permissionCodes) ? [...s.permissionCodes] : [],
      fullAccess: primary === 'admin' && s.fullAccess === true,
      homeRoute: s.homeRoute,
      needsRoleChoice: s.needsRoleChoice,
      hasPassword: s.hasPassword ?? true,
    };
    if (!officer.homeRoute && officer.activeRole) {
      officer.homeRoute = homeRouteForOfficer(officer);
    }
    return officer;
  }

  /** Reload session flags (e.g. hasPassword) from GET /auth/session. */
  async refreshSessionFromApi(): Promise<void> {
    const token = this.accessToken();
    if (!token) return;
    try {
      const data = await firstValueFrom(this.api.get<{ session: ApiSession }>('/auth/session'));
      this.applySession(this.mapSession(data.session), token);
    } catch {
      /* keep local session */
    }
  }

  private errMessage(err: unknown): string {
    return userFacingMessage(err, readDeskLang());
  }

  private readSession(): Officer | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY);
      if (!raw) return null;
      return JSON.parse(raw) as Officer;
    } catch {
      return null;
    }
  }

  private readToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  }
}
